import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";

export const CartDrawer = () => {
  const { items, open, setOpen, subtotal, updateQty, removeItem } = useCart();
  const { format: money } = useCurrency();

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[80]">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="absolute right-0 top-0 h-full w-full max-w-md bg-white flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 py-6 border-b border-[#E5E5E5]">
              <h3 className="eyebrow">Your Bag ({items.length})</h3>
              <button onClick={() => setOpen(false)} aria-label="Close cart" data-testid="cart-close">
                <X strokeWidth={1} className="h-5 w-5" />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-6 px-6 text-center">
                <p className="font-serif text-2xl">Your bag is empty</p>
                <p className="text-sm text-[#737373]">Discover our timeless everyday essentials.</p>
                <button className="btn-primary" onClick={() => setOpen(false)}>Continue Shopping</button>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-4 divide-y divide-[#E5E5E5]">
                  {items.map((i) => (
                    <div key={`${i.product_id}-${i.color}-${i.size}`} className="flex gap-4 py-5" data-testid="cart-line-item">
                      <Link to={`/product/${i.slug}`} onClick={() => setOpen(false)} className="w-24 aspect-[3/4] bg-beige rounded-sm overflow-hidden shrink-0">
                        <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                      </Link>
                      <div className="flex-1 flex flex-col">
                        <div className="flex justify-between gap-2">
                          <div>
                            <p className="font-serif text-lg leading-tight">{i.name}</p>
                            {(i.color || i.size) && <p className="text-xs text-[#737373] mt-1">{[i.color, i.size].filter(Boolean).join(" · ")}</p>}
                          </div>
                          <button onClick={() => removeItem(i.product_id, i.color, i.size)} aria-label="Remove" data-testid="cart-remove-item">
                            <Trash2 strokeWidth={1} className="h-4 w-4 text-[#737373] hover:text-[#1A1A1A]" />
                          </button>
                        </div>
                        <div className="mt-auto flex items-center justify-between">
                          <div className="flex items-center border border-[#E5E5E5] rounded-full">
                            <button className="px-2.5 py-1.5" onClick={() => updateQty(i.product_id, i.color, i.size, i.quantity - 1)} aria-label="Decrease">
                              <Minus strokeWidth={1} className="h-3.5 w-3.5" />
                            </button>
                            <span className="text-sm w-6 text-center">{i.quantity}</span>
                            <button className="px-2.5 py-1.5" onClick={() => updateQty(i.product_id, i.color, i.size, i.quantity + 1)} aria-label="Increase">
                              <Plus strokeWidth={1} className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <span className="text-sm">{money(i.price * i.quantity)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-[#E5E5E5] px-6 py-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="eyebrow">Subtotal</span>
                    <span className="text-lg" data-testid="cart-subtotal">{money(subtotal)}</span>
                  </div>
                  <p className="text-xs text-[#737373]">Shipping & taxes calculated at checkout.</p>
                  <Link to="/cart" onClick={() => setOpen(false)} className="btn-primary w-full" data-testid="cart-checkout-button">
                    Checkout <ArrowRight strokeWidth={1.2} className="h-4 w-4 ml-2" />
                  </Link>
                  <button onClick={() => setOpen(false)} className="w-full text-center eyebrow py-2">
                    Continue Shopping
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
